import { HStack, Text, VStack } from '@ayble-health/dashboard-components';
import React from 'react';
import { Outlet } from 'react-router-dom';
import { Box, Flex } from '@chakra-ui/react';
import { Input as UntypedInput } from '../Form/Input';
import HeaderNav from '../HeaderNav/HeaderNav';
import { Loader } from '../Loader';
import { FilterSidebar } from './FilterSidebar';
import { HealthCoachDashboardList } from './HealthCoachDashboardList';
import { PaginationFooter } from './PaginationFooter';
import { ROW_PADDING_X } from './constants';
import { useHealthCoachDashboard } from './hooks/useHealthCoachDashboard';

// Form/Input is untyped JS; default params make TS infer several props as `undefined` only.
const Input = UntypedInput as React.ComponentType<{
  debounceTime?: number;
  height?: number;
  isClearable?: boolean;
  maxWidth?: string | number;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: string;
  value?: string;
}>;

export const HealthCoachDashboard = () => {
  const {
    isLoadingUsers,
    isFetchingUsers,
    filteredUsers,
    selectedMeetings,
    selectedMeetingNumDays,
    onCareModelChange,
    onRiskStratificationChange,
    onCoachChange,
    onFilterChange,
    onMeetingsFilterChange,
    onUserTypeChange,
    onProviderChange,
    onMeetingConfirmedChange,
    onEnrollmentStatusChange,
    onOnboardingStatusChange,
    onInitialVisitStatusChange,
    onPageChange,
    clearAllFilters,
    page,
    hasNextPage,
    hasPreviousPage,
    totalPages,
    totalResults,
    searchFilter,
    selectedCareModel,
    selectedRiskStratification,
    selectedCoach,
    selectedUserType,
    selectedProvider,
    selectedMeetingConfirmed,
    selectedEnrollmentStatus,
    selectedOnboardingStatus,
    selectedInitialVisitStatus,
    onSortChange,
    sortBy,
    sortOrder,
  } = useHealthCoachDashboard();

  const showInitialLoader = isLoadingUsers && !filteredUsers.length;

  return (
    <div
      className="health-coach-dashboard"
      style={{
        height: '100vh',
        minHeight: 0,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <HeaderNav page={1} hideBack />

      {showInitialLoader ? (
        <Loader loading={isLoadingUsers} />
      ) : (
        <Flex flex={1} minH={0} minW={0}>
          <FilterSidebar
            selectedMeetings={selectedMeetings}
            selectedMeetingNumDays={selectedMeetingNumDays}
            onCareModelChange={onCareModelChange}
            onRiskStratificationChange={onRiskStratificationChange}
            onCoachChange={onCoachChange}
            onMeetingsFilterChange={onMeetingsFilterChange}
            onUserTypeChange={onUserTypeChange}
            onProviderChange={onProviderChange}
            onMeetingConfirmedChange={onMeetingConfirmedChange}
            onEnrollmentStatusChange={onEnrollmentStatusChange}
            onOnboardingStatusChange={onOnboardingStatusChange}
            onInitialVisitStatusChange={onInitialVisitStatusChange}
            clearAllFilters={clearAllFilters}
            selectedCareModel={selectedCareModel}
            selectedRiskStratification={selectedRiskStratification}
            selectedCoach={selectedCoach}
            selectedUserType={selectedUserType}
            selectedProvider={selectedProvider}
            selectedMeetingConfirmed={selectedMeetingConfirmed}
            selectedEnrollmentStatus={selectedEnrollmentStatus}
            selectedOnboardingStatus={selectedOnboardingStatus}
            selectedInitialVisitStatus={selectedInitialVisitStatus}
          />

          <VStack
            flex={1}
            minW={0}
            minH={0}
            pt={4}
            pb={2}
            gap={0}
            align="stretch"
          >
            <HStack
              justifyContent="space-between"
              alignItems="center"
              gap={8}
              px={ROW_PADDING_X}
              mb={4}
              flexShrink={0}
            >
              <Box width="400px" maxW="100%" flexShrink={0}>
                <Input
                  debounceTime={500}
                  height={38}
                  isClearable
                  maxWidth="400px"
                  onChange={onFilterChange}
                  placeholder="Email, name, Ayble ID or Healthie ID"
                  type="text"
                  value={searchFilter}
                />
              </Box>

              <VStack gap={0} align="flex-start">
                <Text
                  my={0}
                  fontSize="3xl"
                  fontWeight="semibold"
                  color="secondary.500"
                  lineHeight="shorter"
                >
                  {totalResults || 0}
                </Text>
                <Text
                  my={0}
                  fontSize="sm"
                  color="secondary.500"
                  whiteSpace="nowrap"
                >
                  Total results
                </Text>
              </VStack>
            </HStack>

            <Box
              flex={1}
              minW={0}
              minH={0}
              display="flex"
              flexDirection="column"
              position="relative"
              aria-busy={isFetchingUsers}
            >
              <Box
                flex={1}
                minH={0}
                opacity={isFetchingUsers ? 0.5 : 1}
                transition="opacity 0.15s"
                pointerEvents={isFetchingUsers ? 'none' : 'auto'}
              >
                <HealthCoachDashboardList
                  users={filteredUsers}
                  isFetching={isFetchingUsers}
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSortChange={onSortChange}
                />
              </Box>

              {isFetchingUsers && (
                <Box
                  position="absolute"
                  inset={0}
                  zIndex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Loader loading noText size="lg" p={0} my={0} />
                </Box>
              )}
            </Box>

            <PaginationFooter
              page={page}
              totalPages={totalPages}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
              onPageChange={onPageChange}
            />
          </VStack>
        </Flex>
      )}

      <Outlet />
    </div>
  );
};
